import { useEffect, useState } from 'react';
import { Banner, Card, Heading, Link, Spinner, Text, VStack } from '@astryxdesign/core';
import { useSearchParams } from 'react-router';

import { api } from '../lib/api';
import { messageOf, refreshMe } from '../stores/auth';

type Status = 'pending' | 'verified' | 'already_verified' | 'error';

/**
 * Public page behind the link in the V1 email — the device that opens it may
 * not hold the session that created the account, so this never assumes login.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('pending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const uid = params.get('uid');
    const token = params.get('token');
    if (!uid || !token) {
      setStatus('error');
      setError('This verification link is missing information.');
      return;
    }
    let cancelled = false;
    api
      .verifyEmail(uid, token)
      .then((res) => {
        if (cancelled) return;
        setStatus(res.status);
        // Picks up email_verified immediately if this browser also holds the
        // session — harmless no-op if it doesn't.
        void refreshMe();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStatus('error');
        setError(messageOf(err));
      });
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <Card padding={4} variant="default" className="auth-card">
      <VStack gap={3}>
        <Heading level={1}>Email verification</Heading>

        {status === 'pending' && <Spinner size="lg" label="Confirming your email" />}

        {(status === 'verified' || status === 'already_verified') && (
          <Banner
            status="success"
            title="Email confirmed"
            description={
              status === 'verified'
                ? 'Your email is verified. You now have full access to your account.'
                : 'This email was already verified.'
            }
          />
        )}

        {status === 'error' && (
          <Banner
            status="error"
            title="Could not verify email"
            description={error ?? 'This link is invalid or has expired.'}
          />
        )}

        <Text size="sm" color="secondary">
          <Link href="/app">Go to your dashboard</Link>
        </Text>
      </VStack>
    </Card>
  );
}
